import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { HrService, hrErrorMessage } from '../hr.service';
import { SensitiveAccessService } from '../sensitive-access.service';
import { CommonService } from '@app/common/common.service';
import { LoginService } from '@app/common/login.service';
import { AlertService } from '@app/shared/alert.service';
import {
	HR_ABSENCE_STATE_LABELS, HR_DOC_FOLDERS, HR_DOC_FOLDER_CONTRACT, HR_DOC_FOLDER_OTHER,
	HR_LEAVE_PERIOD_STATE_LABELS,
	HrAbsence, HrActivity, HrAvailability, HrClock, HrContract, HrCounter, HrDayValidation, HrDocument, HrEmployee,
	HrLeavePeriod, HrMeal, HrOverride, HrSchedule, hrCpSummary, hrEmployeeColor, hrIsHrManager, hrMonthsElapsed,
	hrPeriodLabel, hrResolveActivities, hrScheduleActivityId,
} from '@app/shared/_models/hr';
import { AttachService } from '@app/shared/attach/attach.service';
import { environment } from '@app/../environments/environment';
import { Attach, C_value, Store, User } from '@app/shared/_models/common';
import {
	C_VALUES_HR_ABSENCE_TYPE,
	C_VALUES_USER_ROLE, EVENTTYP_DELETE,
	EVENTTYP_INSERT, EVENTTYP_UPDATE, HR_ABSENCE_STATE_APPROVED, HR_ABSENCE_STATE_REQUESTED,
	HR_ABSENCE_TYPE_CP, HR_AVAILABILITY_AVAILABLE, HR_AVAILABILITY_UNAVAILABLE,
	HR_COUNTER_KIND_MANUAL, HR_PERSON_COLORS, OBJECTTYP_HR_ABSENCE, OBJECTTYP_HR_AVAILABILITY,
	OBJECTTYP_HR_COUNTER, OBJECTTYP_HR_EMPLOYEE, ObjectType,
} from '@app/shared/constants';
import { FormControl } from '@angular/forms';
import { nowIso, pickedDateStr, todayStr, wallMinutesOf } from '@app/shared/time';
import { CounterCell, CounterGranularity, buildCounter, counterLabel } from '../hr-counter';
import { restDecisionMap } from '../hr-payroll-weeks';
import { HrTimeSource, PayrollRules, hrPayrollRulesOf } from '../hr-payroll';
import { hrIdccOf, hrLegifranceSearchUrl } from '../hr-idcc-catalog';
import {
	ContractKind, EmployeeStatus, HR_FORFAIT_DAYS_DEFAULT, OvertimeDestination, OvertimeLeaveBasis, TimeScheme,
	WorkTimeIssue, avg12Issues, countsHours, effectiveWorkRules, employeeWorkProfileOf, timeSchemeForcedBy,
} from '../hr-worktime';
import { availabilityLabel } from '../hr-availability';
import { HrTextOnPipe } from '../hr-text-on.pipe';
import { HR_DAY_LABELS } from '../schedule/schedule.component';
import { EffectiveSlot, effectiveSlotsForRange, frenchHolidaysOf, isoWeekdayOf, minutesOfHhmm } from '../hr-schedule-engine';
import { ForfaitReport, buildForfaitReport } from '../hr-forfait';
import { hrCountedLeaveDays, hrGuessRestWeekdays, hrLeaveBreakdown } from '../hr-leave';
import { closedHolidaysInRange } from '../hr-holidays';
import { WorkPeriod, pairClocks } from '../hr-timesheet';
import { HR_LEAVE_RULES_DEFAULT, LeaveRules, leaveReferencePeriodOf, leaveRulesOf } from '../hr-leave-rights';
import {
	LeaveClosingRow, buildLeaveClosingRow, daysLabel, employeeCarryoverOf,
	leaveClosingWindowForPeriod, leavePeriodLabel, leavePeriodShortLabel,
} from '../hr-leave-closing';
import { hrPaidLeaveTypeIds } from '../hr-absence-catalog';
import { absenceQuotaAccountOf, absenceQuotaIssue, absenceQuotaOf } from '../hr-absence-quota';

// Total de jours d'absence approuvés de l'année, par type (affiché sur la fiche).
export interface AbsenceCounter { typ: number; label: string; days: number; }

// Une ligne de l'historique d'absences de la fiche (tous états, toutes années).
export interface EmployeeAbsenceRow {
	absence: HrAbsence;
	typLabel: string;
	typColor: string;
	period: string;
	days: number;
	state: number;
	stateLabel: string;
}

// Un service (« poste ») vu DEPUIS cette fiche : ouvert à la personne, ou bloqué et pourquoi.
// C'est ce bloc qui explique qu'un service existant (ex. « Entrepôt & nettoyage ») n'apparaisse
// pas dans le select « Emploi du temps » : il n'a encore aucun horaire.
export interface PostRow {
	activity: HrActivity;
	scheduleName: string | null;		// horaires types existants pour ce service
	id_schedule: number | null;
	blocked: string;								// '' = poste ouvert ; sinon la raison, en clair
}

export interface PostGroup { store: Store; isDefault: boolean; posts: PostRow[]; }

// ============================================================
// « Exporter mes données (RGPD) » — le dossier remis à la personne
// ============================================================
//
// Droit d'ACCÈS (art. 15 RGPD) : toute personne obtient copie des données traitées la
// concernant. Droit à la PORTABILITÉ (art. 20) : ces données lui sont remises « dans un format
// structuré, couramment utilisé et lisible par machine » — le JSON coche les trois cases.
// L'assemblage est PUR et côté client : chaque section reprend TELLE QUELLE une réponse du
// serveur, déjà expurgée et filtrée par le jeton (keep_mine) — l'export ne montre jamais plus
// que ce que l'écran avait le droit d'afficher. En particulier `data.pay` n'y figure pas :
// le serveur ne le sert à personne d'autre que le responsable RH (régime `pay`).
export function hrMyDataExport(fiche: HrEmployee, parts: {
	absences: HrAbsence[]; clocks: HrClock[]; counters: HrCounter[];
	meals: HrMeal[]; contracts: HrContract[]; leavePeriods: HrLeavePeriod[];
}, generatedAt: string): Record<string, unknown> {
	return {
		export: {
			titre: 'Mes données RH',
			base: 'Droit d\'accès (art. 15 RGPD) et droit à la portabilité (art. 20 RGPD)',
			genere_le: generatedAt,
		},
		fiche,															// la fiche telle que servie (expurgée serveur)
		absences: parts.absences,						// demandes et arrêts, tous états
		pointages: parts.clocks,						// entrées/sorties de la pointeuse
		compteurs: parts.counters,					// ajustements du compteur d'heures
		repas: parts.meals,									// repas ouvrant droit au titre-restaurant
		contrats: parts.contracts,					// périodes contractuelles (registre entrées/sorties)
		clotures_cp: parts.leavePeriods,		// arrêtés de compte des années de congés
	};
}

// Fiche salarié — /hr/employee/:id (0 = création).
@Component({
	selector: 'app-hr-employee',
	standalone: true,
	imports: [DecimalPipe, RouterLink, MenuCommonComponent, ReactiveFormsModule, MatFormFieldModule, MatInputModule,
		MatSelectModule, MatButtonModule, MatButtonToggleModule, MatIconModule, MatDatepickerModule,
		MatSlideToggleModule, MatCheckboxModule, MatTooltipModule, HrTextOnPipe],
	templateUrl: './employee.component.html',
	styleUrl: './employee.component.scss',
	// La gouttière sous un champ mesure la hauteur réelle du hint au lieu des 16 px
	// fixes de Material : un hint sur deux lignes pousse la suite au lieu de la
	// recouvrir, et un champ sans hint ne réserve plus de vide.
	changeDetection: ChangeDetectionStrategy.Eager,
	providers: [{
		provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
		useValue: { subscriptSizing: 'dynamic' } as MatFormFieldDefaultOptions,
	}],
})
export class EmployeeComponent implements OnInit, OnDestroy {

	public hrService = inject(HrService);
	public sensitive = inject(SensitiveAccessService);		// déverrouillage des données salariales (mot de passe)
	private commonService = inject(CommonService);
	private loginService = inject(LoginService);
	private alertService = inject(AlertService);
	private attachService = inject(AttachService);
	private route = inject(ActivatedRoute);
	private router = inject(Router);

	public formGroup: FormGroup = HrEmployee.asFormGroup();
	public users: User[] = [];
	public stores: Store[] = [];
	public schedules: HrSchedule[] = [];
	public activities: HrActivity[] = [];
	public absenceCounters: AbsenceCounter[] = [];
	public saving = false;

	// ============================================================
	// QUI REGARDE CETTE FICHE ?
	// ============================================================
	//
	// `isHrManager` : responsable RH ou Super Admin — la fiche complète, en écriture.
	// `isSelf`      : le salarié sur SA propre fiche — il y voit ce qui le concerne (contact,
	//                 solde de congés, ses heures, ses absences) et ne peut modifier QUE ses
	//                 coordonnées. Contrat, salaire, affectation, rôles et code de pointage ne
	//                 sont même pas montés : ce sont des éléments de son contrat, pas des
	//                 champs de formulaire.
	// Ni l'un ni l'autre ⇒ la fiche ne s'ouvre pas (le serveur ne l'aurait de toute façon pas
	// détaillée : /hr/employees ne rend les données personnelles qu'à l'intéressé).
	public isHrManager = false;
	public isSelf = false;

	// La fiche commence par LA personne : soit un utilisateur existant (select), soit une
	// nouvelle personne créée ici même (c_user minimal sans login), liée aussitôt.
	public personMode: 'existing' | 'new' = 'existing';
	public newUserFirst = new FormControl<string | null>(null);
	public newUserLast = new FormControl<string | null>(null);
	public creatingUser = false;

	// Rôles de la personne — portés par le c_user (référentiel c_values 20), PAS par la fiche :
	// écriture immédiate (UPDATE USER partiel), comme la matrice « Personnel & rôles ».
	public roles: C_value[] = [];
	public userRoles = new Set<number>();
	public savingRoles = false;

	// Un code de pointage est-il défini ? C'est la SEULE chose que le serveur dise du PIN : il est
	// stocké haché et n'est jamais renvoyé. Pilote le libellé du champ et le bouton « Retirer ».
	public hasPin = false;

	// Données RH complémentaires — rangées dans hr_employee.data (JSONB, aucun schéma serveur) :
	// contact facultatif (simplification dev), sortie, config CP.
	public phoneCtrl = new FormControl<string | null>(null);
	public emailCtrl = new FormControl<string | null>(null);
	// IDENTITÉ DE PAIE — civilité et date de naissance. Elles ne servent pas au planning : elles
	// servent aux TIERS. L'émetteur de titres-restaurant les exige dans son fichier de commande
	// (civilité en toutes lettres, naissance en jj/mm/aaaa), et une commande à laquelle elles
	// manquent est rejetée à l'import.
	// Le MATRICULE, lui, existe déjà : c'est `pay.payroll_code` (« Code paie »), la clé de
	// rapprochement avec le logiciel de paie. Un second identifiant n'aurait fait que diverger.
	public genderCtrl = new FormControl<string | null>(null);		// 'M' | 'F'
	public birthDateCtrl = new FormControl<string | Date | null>(null);
	// Adresse postale (data.address) — reprise des bulletins de paie, et attendue sur tout
	// document RH (contrat, attestation). Aucun schéma serveur : le JSONB transite tel quel.
	public streetCtrl = new FormControl<string | null>(null);
	public zipCtrl = new FormControl<string | null>(null);
	public cityCtrl = new FormControl<string | null>(null);
	public leaveDateCtrl = new FormControl<string | Date | null>(null);
	public cpBaseDaysCtrl = new FormControl<number | null>(null);
	public cpBaseDateCtrl = new FormControl<string | Date | null>(null);
	// Valeur d'attente seulement : le chargement la remplace par celle de l'établissement, ou par
	// le taux propre à la personne s'il en a un.
	public cpMonthlyCtrl = new FormControl<number | null>(HR_LEAVE_RULES_DEFAULT.days_per_month);
	// CONGÉS NON PRIS EN FIN D'ANNÉE — la règle PROPRE à ce salarié, quand il en a une.
	//
	// Décochée (le cas normal), la fiche SUIT l'établissement : changer la règle maison la change
	// pour tout le monde. C'est le contraire du piège de `data.cp.monthly`, qui était recopié sur
	// chaque fiche et y figeait 2,5 j/mois pour l'éternité.
	//
	// Et ce n'est qu'une PROPOSITION : ce qui décide réellement du sort des jours, c'est l'arrêté
	// de compte de l'année, pris année par année (hr_leave_period) — parce que la raison d'un
	// report appartient à l'ANNÉE (maladie, congés refusés), pas à la personne.
	public cpCarryoverOwnCtrl = new FormControl<boolean>(false);
	public cpCarryoverCtrl = new FormControl<'carry' | 'lost'>('carry');

	// Données salariales (hr_employee.data.pay) — L1.2. Ce que le rapport de paie exige pour
	// considérer un profil complet, plus ce que le comptable réclame en pratique.
	// Documents RH (L5) — pièces c_attach classées par dossier.
	public documents: HrDocument[] = [];
	public docFolderCtrl = new FormControl<number>(HR_DOC_FOLDER_CONTRACT);
	public docExpiryCtrl = new FormControl<string | Date | null>(null);
	public uploadingDoc = false;
	public readonly docFolders = HR_DOC_FOLDERS;

	// Disponibilités déclarées (L4) — informatif sur le planning, jamais bloquant.
	public availabilities: HrAvailability[] = [];
	public availKindCtrl = new FormControl<number>(HR_AVAILABILITY_UNAVAILABLE);
	public availWeekdayCtrl = new FormControl<number | null>(null);
	public availStartCtrl = new FormControl<string | null>(null);
	public availEndCtrl = new FormControl<string | null>(null);
	public availUntilCtrl = new FormControl<string | Date | null>(null);
	public availCommentCtrl = new FormControl<string | null>(null);
	public readonly dayLabels = HR_DAY_LABELS;
	public readonly AVAIL_UNAVAILABLE = HR_AVAILABILITY_UNAVAILABLE;
	public readonly AVAIL_AVAILABLE = HR_AVAILABILITY_AVAILABLE;

	// Compteur d'heures (L2) — le « combien d'heures il a en stock ».
	public counters: HrCounter[] = [];
	public clocks: HrClock[] = [];
	public validations: HrDayValidation[] = [];		// journées arbitrées — elles font foi dans le compteur
	public overrides: HrOverride[] = [];
	public counterCells: CounterCell[] = [];
	// Dépassements de la moyenne sur 12 semaines glissantes (L3121-22) — cf. rebuildAvg12.
	public avg12Issues: WorkTimeIssue[] = [];
	public counterGranularity: CounterGranularity = 'month';
	// Mode de décompte de l'établissement (règles de paie) : planning ou pointeuse.
	public timeSource: HrTimeSource = 'planning';
	public counterOpen = false;
	public adjustHoursCtrl = new FormControl<number | null>(null);
	public adjustCommentCtrl = new FormControl<string | null>(null);

	// Couleur d'identité de la personne (hr_employee.data.color) — distingue les gens en vue Postes.
	public colorCtrl = new FormControl<string | null>(null);

	// N2 — surcharges INDIVIDUELLES du temps de travail (hr-worktime.ts). Un contrat peut être plus
	// favorable que la convention, et un temps partiel a des seuils qui lui sont propres.
	private payrollRules: PayrollRules = hrPayrollRulesOf(null);
	// Période de référence des congés payés : elle décide de la fenêtre des congés PLAFONNÉS dont
	// l'accord cale le droit sur elle plutôt que sur l'année civile (hr-absence-quota.ts).
	private leaveRules: LeaveRules = leaveRulesOf(null);
	// Heures mises en récupération par le rapport de paie — clé « id_employee|lundi|taux|nature ».
	private restDecided = new Map<string, number>();
	// LES DEUX AXES INDÉPENDANTS (cf. l'encadré de hr-worktime.ts) : le statut classe la personne,
	// le décompte dit si ses heures se comptent. Croire que « cadre » implique « pas d'heures sup »
	// est l'erreur classique — et coûteuse : un cadre intégré à l'horaire collectif se voit devoir
	// ses majorations comme tout le monde (Cass. soc., constant).
	public statusCtrl = new FormControl<EmployeeStatus>('non_cadre');
	public timeSchemeCtrl = new FormControl<TimeScheme>('hours');
	public forfaitDaysCtrl = new FormControl<number | null>(null);
	public contractKindCtrl = new FormControl<ContractKind | null>(null);
	public complementaryLimitCtrl = new FormControl<number | null>(null);
	public weekWorkingDaysCtrl = new FormControl<number | null>(null);
	// Base journalière en HEURES, saisie directement quand la division ne tombe pas juste (un
	// contrat de 32 h sur 4 jours de 8 h, un 35 h annoncé « 7 h par jour »…).
	public dayBaseHoursCtrl = new FormControl<number | null>(null);
	public rcrDefaultCtrl = new FormControl<OvertimeDestination | null>(null);
	public leaveBasisCtrl = new FormControl<OvertimeLeaveBasis | null>(null);
	public readonly palette = HR_PERSON_COLORS;

	public contractTypeCtrl = new FormControl<string | null>(null);
	public grossMonthlyCtrl = new FormControl<number | null>(null);
	public qualificationCtrl = new FormControl<string | null>(null);
	public trialEndCtrl = new FormControl<string | Date | null>(null);
	public mealVouchersCtrl = new FormControl<boolean>(true);
	// Carte physique à commander chez l'émetteur — une PREMIÈRE commande seulement : ensuite le
	// crédit mensuel se recharge sur la carte déjà en main. Reste donc à false par défaut.
	public mealPhysicalCardCtrl = new FormControl<boolean>(false);
	// Identification paie et classification conventionnelle (IDCC 1982) — lues sur le bulletin.
	// `payroll_code` est LA clé de rapprochement avec le logiciel de paie : sans elle, retrouver
	// « qui est le 0039 » sur un bulletin Sage se fait à la main.
	public payrollCodeCtrl = new FormControl<string | null>(null);
	public hourlyRateCtrl = new FormControl<number | null>(null);
	public levelCtrl = new FormControl<string | null>(null);
	public coefficientCtrl = new FormControl<string | null>(null);
	public positionCtrl = new FormControl<string | null>(null);
	public readonly contractTypes = ['CDI', 'CDD', 'Apprentissage', 'Professionnalisation', 'Stage', 'Intérim', 'Extra'];

	// La convention collective de l'établissement, telle qu'elle doit apparaître SUR LA FICHE :
	// un salarié a le droit de savoir quel texte régit son contrat et d'aller le lire. Le lien
	// Légifrance vient de la base KALI de l'État (identifiant KALICONT), le PDF est celui que
	// l'entreprise a déposé — tous deux réglés une seule fois dans Organisation ▸ Règles de paie.
	get convention(): { idcc: string; label: string; url: string; pdf: string; title: string } | null {
		const r = this.payrollRules;
		if (!r.idcc) return null;
		const e = hrIdccOf(r.idcc);
		return {
			idcc: r.idcc,
			label: r.idcc_label || e?.short || '',
			url: r.idcc_url || e?.url || hrLegifranceSearchUrl(r.idcc),
			pdf: r.idcc_pdf,
			title: e?.title ?? '',
		};
	}

	// Absences : historique complet + ligne de saisie inline (reprise de données — on enchaîne
	// les arrêts sans jamais ouvrir de fenêtre).
	public absenceTypes: C_value[] = [];
	public absenceRows: EmployeeAbsenceRow[] = [];
	public showAllYears = false;
	public addingAbsence = false;
	public newAbsTyp = new FormControl<number | null>(null);
	public newAbsStart = new FormControl<string | Date | null>(null);
	public newAbsEnd = new FormControl<string | Date | null>(null);
	public newAbsComment = new FormControl<string>('');

	public current?: HrEmployee;					// fiche chargée (préserve le data existant au merge ; lue par le template pour le compteur)
	private allAbsences: HrAbsence[] = [];	// TOUS états, plage large (compteur CP + historique)
	// ARRÊTÉS DE COMPTE des années de congés — le seul chiffre stocké du compteur de CP, et la
	// seule chose qui relie une année à la suivante (cf. hr-leave-closing.ts).
	public leavePeriods: HrLeavePeriod[] = [];

	private routeSub?: Subscription;
	private userSub?: Subscription;

	ngOnInit(): void {
		// Changer la personne liée change les rôles affichés (ils vivent sur le c_user).
		// Posé AVANT le chargement : rebuildForm() le remplace ensuite sur le formGroup rechargé.
		this.userSub = this.formGroup.get('id_user')!.valueChanges.subscribe(() => this.syncUserRoles());
		this.routeSub = this.route.paramMap.subscribe(params => this.load(+(params.get('id') ?? 0)));
	}

	ngOnDestroy(): void {
		this.routeSub?.unsubscribe();
		this.userSub?.unsubscribe();
	}

	private load(id: number): void {
		const year = +todayStr().slice(0, 4);		// année MÉTIER, pas celle de l'horloge du poste
		forkJoin({
			employees: this.hrService.getEmployees$(),
			schedules: this.hrService.getSchedules$(),
			activities: this.hrService.getActivities$(),
			users: this.hrService.getUsers$(),
			stores: this.commonService.getStores(),
			// Référentiels c_values : valuesOf$ porte les deux pièges du forkJoin (flux qui ne
			// complète jamais, échec qui fige l'écran) — cf. hr.service.ts.
			roles: this.hrService.valuesOf$(C_VALUES_USER_ROLE),
			absenceTypes: this.hrService.valuesOf$(C_VALUES_HR_ABSENCE_TYPE),
			// Mode de décompte : le compteur d'heures doit dire la même chose que le rapport de paie.
			payrollRules: this.hrService.payrollRules$(),
			// … et les heures que le responsable RH a mises en récupération, mois par mois. Sans
			// elles, la fiche afficherait la politique de l'établissement pendant que la paie
			// applique une décision contraire — deux soldes pour un seul salarié.
			payrollWeeks: this.hrService.payrollWeeks$(),
			// plage LARGE et TOUS états : le compteur CP remonte à la date de référence et la
			// reprise de données saisit des arrêts anciens.
			absences: id > 0
				? this.hrService.getAbsences$((year - 5) + '-01-01', (year + 1) + '-12-31', undefined, id)
				: of([] as HrAbsence[]),
			// Compteur d'heures (L2) : ajustements manuels, pointages et exceptions de l'année.
			counters: id > 0 ? this.hrService.getCounters$(year + '-01-01', year + '-12-31', id) : of([] as HrCounter[]),
			clocks: id > 0 ? this.hrService.getClocks$(year + '-01-01', year + '-12-31', id) : of([] as HrClock[]),
			// Journées arbitrées (clôture du responsable, déclaration du salarié) : elles font foi
			// dans le compteur comme en paie — sinon une heure supplémentaire actée n'y figurerait pas.
			validations: id > 0
				? this.hrService.getValidations$(year + '-01-01', year + '-12-31', id).pipe(catchError(() => of([] as HrDayValidation[])))
				: of([] as HrDayValidation[]),
			overrides: id > 0 ? this.hrService.getOverrides$(year + '-01-01', year + '-12-31') : of([] as HrOverride[]),
			availabilities: id > 0 ? this.hrService.getAvailabilities$(id) : of([] as HrAvailability[]),
			documents: id > 0 ? this.hrService.getDocuments$(id) : of([] as HrDocument[]),
			// Clôtures des années de congés — TOUT l'historique : le report d'une année vient de la
			// clôture de la précédente, donc d'avant toute borne qu'on poserait.
			leavePeriods: id > 0
				? this.hrService.getLeavePeriods$(id).pipe(catchError(() => of([] as HrLeavePeriod[])))
				: of([] as HrLeavePeriod[]),
		}).subscribe(({ employees, schedules, activities, users, stores, roles, absenceTypes, payrollRules, payrollWeeks, absences, counters, clocks, validations, overrides, availabilities, documents, leavePeriods }) => {
			this.leavePeriods = leavePeriods;
			this.validations = validations;
			// Toutes les mises en récupération enregistrées, à plat : le compteur les applique
			// exactement comme le rapport de paie (cf. `restDecided` de hr-counter.ts).
			this.restDecided = new Map(payrollWeeks
				.flatMap(c => Array.from(restDecisionMap(c.rest).entries())));
			this.payrollRules = payrollRules.rules;
			this.leaveRules = payrollRules.leave;
			this.timeSource = this.payrollRules.time_source;
			this.counters = counters;
			this.clocks = clocks;
			this.overrides = overrides;
			this.availabilities = availabilities;
			this.documents = documents;
			this.schedules = schedules;
			this.activities = hrResolveActivities(activities, schedules);
			this.stores = stores;
			this.isHrManager = hrIsHrManager(this.loginService.loggeduser, users);		// la base fait foi (vieux JWT sans roles)
			this.roles = (roles as C_value[]).filter(r => r.is_active !== false);
			this.absenceTypes = (absenceTypes as C_value[]).filter(t => t.is_active !== false);
			const employee = employees.find(e => e.id === id);
			this.isSelf = !!employee && employee.id_user === this.loginService.loggeduser?.id;
			// La fiche de quelqu'un d'autre, sans être responsable RH : on n'ouvre pas. Le garde de
			// route couvre déjà /hr/employees, mais /hr/employee/:id reste ouvert exprès (chacun
			// doit pouvoir atteindre la sienne) — c'est donc ici que la porte se ferme.
			if (!this.isHrManager && !this.isSelf) {
				this.alertService.error('Cette fiche ne vous est pas accessible.');
				this.router.navigate(['/hr/planning']);
				return;
			}
			this.current = employee;
			// À la création : ne proposer que les users sans fiche RH (une fiche par personne).
			const linked = new Set(employees.filter(e => e.id !== id).map(e => e.id_user));
			this.users = users.filter(u => u.id !== undefined && (!linked.has(u.id!) || u.id === employee?.id_user));
			this.rebuildForm(employee);
			this.allAbsences = absences;
			this.rebuildAbsences();
			// En création sans utilisateur libre, la seule voie est la nouvelle personne.
			this.personMode = (!employee && this.users.length === 0) ? 'new' : 'existing';

			// Hydratation des champs rangés dans data (contact, CP) — naissance, sexe et sortie
			// sont des COLONNES depuis le 2026-07-27, elles arrivent au premier niveau de la fiche.
			const d = employee?.data ?? {};
			this.phoneCtrl.setValue(d.phone ?? null);
			this.emailCtrl.setValue(d.email ?? null);
			this.genderCtrl.setValue(employee?.gender ?? null);
			this.birthDateCtrl.setValue(employee?.birth_date ?? null);
			this.streetCtrl.setValue(d.address?.street ?? null);
			this.zipCtrl.setValue(d.address?.zip ?? null);
			this.cityCtrl.setValue(d.address?.city ?? null);
			this.leaveDateCtrl.setValue(employee?.leave_date ?? null);
			this.cpBaseDaysCtrl.setValue(d.cp?.base_days ?? null);
			this.cpBaseDateCtrl.setValue(d.cp?.base_date ?? null);
			// Sans taux propre à la personne, celui de L'ÉTABLISSEMENT : 2,5 j/mois quand les congés
			// se décomptent en jours ouvrables, 2,08 en jours ouvrés (règles c_values 25, réglées
			// dans Établissements & services). Le régime général figé ici affichait 2,5 à tout le
			// monde, y compris aux entreprises qui décomptent en ouvrés — et le chiffre partait
			// ensuite tel quel dans le compteur de CP de la fiche.
			this.cpMonthlyCtrl.setValue(d.cp?.monthly ?? this.leaveRules.days_per_month);
			// Règle propre de report : présente = la case est cochée. Absente, la fiche suit
			// l'établissement, et la liste affiche ce que l'établissement dit — pas un choix mort.
			const own = employeeCarryoverOf(d);
			this.cpCarryoverOwnCtrl.setValue(own !== null);
			this.cpCarryoverCtrl.setValue(own ?? this.leaveRules.carryover_default);
			// Sans couleur choisie, on affiche celle de la palette : le sélecteur montre ce que
			// le planning affiche déjà, plutôt qu'un noir arbitraire.
			this.colorCtrl.setValue(d.color ?? hrEmployeeColor(employee, HR_PERSON_COLORS));
			this.rebuildCounter();
			// Données salariales (L1.2) — alimentent le rapport de paie et son contrôle de complétude.
			this.contractTypeCtrl.setValue(d.pay?.contract_type ?? null);
			this.grossMonthlyCtrl.setValue(d.pay?.gross_monthly ?? null);
			this.qualificationCtrl.setValue(d.pay?.qualification ?? null);
			this.trialEndCtrl.setValue(d.pay?.trial_end ?? null);
			this.mealVouchersCtrl.setValue(d.pay?.meal_vouchers !== false);
			this.mealPhysicalCardCtrl.setValue(d.pay?.meal_physical_card === true);
			this.payrollCodeCtrl.setValue(d.pay?.payroll_code ?? null);
			this.hourlyRateCtrl.setValue(d.pay?.hourly_rate ?? null);
			this.levelCtrl.setValue(d.pay?.level ?? null);
			this.coefficientCtrl.setValue(d.pay?.coefficient ?? null);
			// Statut et décompte : relus par le moteur, qui borne les valeurs aberrantes et force
			// « hors décompte » sur un cadre dirigeant ou un TNS.
			const profile = employeeWorkProfileOf(d);
			this.statusCtrl.setValue(profile.status, { emitEvent: false });
			this.timeSchemeCtrl.setValue(profile.time_scheme, { emitEvent: false });
			this.forfaitDaysCtrl.setValue(profile.forfait_days);
			this.syncSchemeLock();
			// N2 — surcharges du temps de travail (null = la convention de l'établissement s'applique).
			this.contractKindCtrl.setValue(d.work?.contract_kind ?? null);
			this.complementaryLimitCtrl.setValue(d.work?.complementary_limit_ratio ?? null);
			this.weekWorkingDaysCtrl.setValue(d.work?.week_working_days ?? null);
			this.dayBaseHoursCtrl.setValue(d.work?.day_base_min != null ? d.work.day_base_min / 60 : null);
			this.rcrDefaultCtrl.setValue(d.work?.rcr_default ?? null);
			this.leaveBasisCtrl.setValue(d.work?.overtime_leave_basis ?? null);
			this.positionCtrl.setValue(d.pay?.position ?? null);
		});
	}

	// Le formGroup est remplacé à chaque chargement : réabonner l'écoute de la personne liée.
	private rebuildForm(employee: HrEmployee | undefined): void {
		this.formGroup = HrEmployee.asFormGroup(employee ?? undefined);
		this.hasPin = !!employee?.has_pin;			// le code lui-même n'arrive jamais jusqu'ici
		this.userSub?.unsubscribe();
		this.userSub = this.formGroup.get('id_user')!.valueChanges.subscribe(() => this.syncUserRoles());
		this.syncUserRoles();
	}

	// ============================================================
	// Code de pointage — write-only
	// ============================================================
	//
	// Le serveur ne renvoie plus le code (il est stocké haché) : on sait seulement s'il en existe
	// un. Le champ reste donc VIDE à l'ouverture et « enregistrer sans y toucher » conserve le
	// code — sinon chaque modification d'adresse effacerait le moyen de pointer de la personne.

	// Sous le champ : une ligne, pas trois. L'explication complète passe en infobulle (pinTip).
	get pinHint(): string {
		return this.hasPin ? 'Vide = code inchangé' : 'Au moins 4 chiffres';
	}

	get pinTip(): string {
		return this.hasPin
			? 'Un code est déjà défini. Laissez le champ vide pour le conserver, saisissez-en un autre pour le remplacer.'
			: 'Code tapé par la personne sur la tablette partagée du magasin pour pointer.';
	}

	// Retirer le code : on marque le champ à '' (l'effacement explicite du contrat write-only) et
	// on laisse l'utilisateur enregistrer — rien n'est écrit tant qu'il n'a pas validé la fiche.
	onClearPin(): void {
		this.formGroup.get('pin')?.setValue('');
		this.formGroup.markAsDirty();
		this.hasPin = false;
		this.alertService.success('Code retiré — enregistrez la fiche pour confirmer');
	}

	// Les rôles MÉTIER sont désormais sur la FICHE, plus sur le compte (scission du 2026-08-03) :
	// ils décrivent un salarié, pas un accès. On les lit donc dans le formulaire, et non plus dans
	// la liste des users — le `id_user` ne sert plus qu'à savoir si la fiche est rattachée.
	private syncUserRoles(): void {
		this.userRoles = new Set<number>(this.formGroup.get('roles')?.value ?? []);
	}

	// ============================================================
	// Rôles MÉTIER de la personne — hr_employee.roles, enregistrés immédiatement
	// ============================================================

	get linkedUserId(): number | null {
		const id = this.formGroup.get('id_user')?.value;
		return id != null ? +id : null;
	}

	roleChecked(roleId: number): boolean {
		return this.userRoles.has(roleId);
	}

	// Résumé en clair sous les cases : ce que ces rôles autorisent.
	get roleSummary(): string {
		if (!this.linkedUserId) return '';
		if (this.userRoles.size === 0) return 'Aucun rôle : cette personne ne peut être planifiée que dans les services ouverts à tous.';
		return this.roles.filter(r => this.userRoles.has(r.id!)).map(r => r.name).join(', ');
	}

	onToggleRole(roleId: number): void {
		// Une fiche non enregistrée (id 0) n'a pas encore de ligne à mettre à jour : l'event
		// partirait sur un id inexistant et rendrait un 404. Les cases s'ouvrent au premier
		// enregistrement.
		if (!this.formGroup.get('id')?.value || !this.isHrManager || this.savingRoles) return;
		const next = new Set(this.userRoles);
		if (next.has(roleId)) next.delete(roleId); else next.add(roleId);
		const sorted = Array.from(next).sort((a, b) => a - b);
		this.savingRoles = true;
		this.hrService.postEmployeeRoles(this.formGroup.value as HrEmployee, sorted).subscribe({
			next: () => {
				this.userRoles = next;																	// l'UI ne bascule qu'après le serveur
				this.formGroup.get('roles')?.setValue(sorted, { emitEvent: false });		// garde le formulaire cohérent
				this.savingRoles = false;
			},
			error: (e: unknown) => { this.savingRoles = false; this.alertService.error(hrErrorMessage(e)); },
		});
	}

	// ============================================================
	// Affectation — mobilité en cases à cocher, postes ouverts expliqués
	// ============================================================

	get defaultStoreId(): number | null {
		const id = this.formGroup.get('id_store')?.value;
		return id != null ? +id : null;
	}

	isDefaultStore(storeId: number): boolean {
		return this.defaultStoreId === storeId;
	}

	// L'établissement principal est TOUJOURS dans la mobilité (verrouillé coché).
	isInMobility(storeId: number): boolean {
		return this.isDefaultStore(storeId) || (this.formGroup.get('mobility')?.value ?? []).includes(storeId);
	}

	onToggleMobility(storeId: number): void {
		if (this.isDefaultStore(storeId)) return;						// le principal ne se décoche pas ici
		const current: number[] = this.formGroup.get('mobility')?.value ?? [];
		const next = current.includes(storeId) ? current.filter(s => s !== storeId) : [...current, storeId];
		this.formGroup.get('mobility')?.setValue(next.sort((a, b) => a - b));
		this.formGroup.markAsDirty();
	}

	// Emplois du temps groupés par établissement — un select à plat ne dit pas d'où ils viennent.
	get scheduleGroups(): { store: Store; schedules: HrSchedule[] }[] {
		return this.stores
			.map(store => ({
				store,
				schedules: this.schedules.filter(s => s.id_store === store.id && s.is_active !== false),
			}))
			.filter(g => g.schedules.length > 0);
	}

	// Les services des établissements où la personne peut travailler, avec la raison quand le
	// poste n'est pas ouvert (rôle non admis, ou aucun horaire encore créé pour ce service).
	get postGroups(): PostGroup[] {
		const defaultStore = this.defaultStoreId;
		const mobility: number[] = this.formGroup.get('mobility')?.value ?? [];
		const storeIds = new Set<number>(mobility);
		if (defaultStore != null) storeIds.add(defaultStore);
		return this.stores
			.filter(s => s.id != null && storeIds.has(s.id))
			.map(store => ({
				store,
				isDefault: store.id === defaultStore,
				posts: this.activities
					.filter(a => a.id_store === store.id && a.is_active !== false)
					.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0) || a.name.localeCompare(b.name))
					.map(a => this.toPostRow(a)),
			}));
	}

	private toPostRow(a: HrActivity): PostRow {
		const sch = this.schedules.find(s => hrScheduleActivityId(s) === a.id && s.is_active !== false);
		const roleOk = !a.roles?.length || Array.from(this.userRoles).some(r => a.roles.includes(r));
		return {
			activity: a,
			scheduleName: sch?.name ?? null,
			id_schedule: sch?.id ?? null,
			blocked: !roleOk
				? ('réservé aux rôles : ' + this.roleNamesOf(a.roles))
				: (!sch ? 'aucun horaire créé pour ce service' : ''),
		};
	}

	private roleNamesOf(ids: number[]): string {
		return this.roles.filter(r => ids.includes(r.id!)).map(r => r.name).join(', ') || '—';
	}

	// Ouvre l'éditeur d'horaires sur ce service (créer les horaires manquants d'un poste).
	onOpenSchedule(post: PostRow): void {
		this.router.navigate(['/hr/schedule' + (post.id_schedule ? '/' + post.id_schedule : '')]);
	}

	// ============================================================
	// L'ANNÉE DE CONGÉS — N et N-1
	// ============================================================
	//
	// Les congés ne se comptent pas par année civile : ils s'acquièrent sur une période de
	// référence (du 1er juin au 31 mai à défaut d'accord, réglable) et se prennent sur la suivante.
	// La fiche montre donc DEUX choses :
	//   - l'année EN COURS, entièrement recalculée depuis les absences, report entrant compris ;
	//   - l'HISTORIQUE des années arrêtées, tel qu'il a été décidé — pas tel qu'on le recalculerait
	//     aujourd'hui : un arrêté de compte ne se refait pas.
	//
	// Le formulaire de clôture, lui, vit sur /hr/leave-closing : ici on rend compte, on ne décide
	// pas. La fiche est l'endroit où le salarié vient LIRE son solde.

	/// Les jours de CP réellement décomptés du salarié, tous exercices confondus.
	private cpTakenDays(): { dat: string; days: number; waived: boolean }[] {
		const cpTypes = new Set(hrPaidLeaveTypeIds(this.absenceTypes));
		const approved = this.allAbsences.filter(a =>
			a.state === HR_ABSENCE_STATE_APPROVED && a.typ != null && cpTypes.has(a.typ));
		if (approved.length === 0) return [];
		const years = approved.map(a => +a.datestart.slice(0, 4));
		const holidays = new Map<string, string>();
		for (let y = Math.min(...years) - 1; y <= Math.max(...years) + 1; y++) {
			for (const [k, label] of frenchHolidaysOf(y)) holidays.set(k, label);
		}
		const from = (Math.min(...years) - 1) + '-01-01';
		const to = (Math.max(...years) + 2) + '-12-31';
		return hrCountedLeaveDays(approved, from, to, holidays)
			.map(d => ({ dat: d.dat, days: d.days, waived: false }));
	}

	/// L'année de congés EN COURS — recalculée, report entrant compris.
	get cpCurrentYear(): LeaveClosingRow | null {
		if (!this.current?.id) return null;
		const period = leaveReferencePeriodOf(todayStr(), this.leaveRules);
		return buildLeaveClosingRow({
			id_employee: this.current.id,
			hire_date: this.current.hire_date ?? null,
			// La sortie borne l'acquisition (L3141-3) : la fiche d'un sorti montre son vrai solde.
			leave_date: this.current.leave_date ?? null,
			carryover: employeeCarryoverOf(this.current.data),
			taken: this.cpTakenDays(),
			closings: this.leavePeriods,
		}, leaveClosingWindowForPeriod(period, todayStr(), this.leaveRules), this.leaveRules);
	}

	/// L'historique des années ARRÊTÉES, la plus récente d'abord — tel qu'il a été décidé.
	get cpClosedYears(): { label: string; period: string; carried: number; lost: number; paid: number; state: number; stateLabel: string; comment: string | null }[] {
		return [...this.leavePeriods]
			.sort((a, b) => b.datstart.localeCompare(a.datstart))
			.map(p => ({
				label: leavePeriodShortLabel({ from: p.datstart, to: p.datend }),
				period: leavePeriodLabel({ from: p.datstart, to: p.datend }),
				carried: +p.carried_days || 0,
				lost: +p.lost_days || 0,
				paid: +p.paid_days || 0,
				state: p.state,
				stateLabel: HR_LEAVE_PERIOD_STATE_LABELS[p.state] ?? ('État ' + p.state),
				comment: p.comment,
			}));
	}

	public readonly cpDaysLabel = daysLabel;
	public readonly cpPeriodShortLabel = leavePeriodShortLabel;

	// ============================================================
	// Compteur de CP HÉRITÉ — la reprise saisie sur la fiche
	// ============================================================
	//
	// Antérieur à la clôture d'année : « au 1er juin il lui était dû 12,5 jours », saisi ici même.
	// C'est une clôture de période écrite au mauvais endroit — elle ne dit pas DE QUELLE année
	// viennent ces jours, ni qui l'a arrêtée. Conservé tant que des fiches en portent (le compteur
	// ci-dessus prend le relais dès qu'une clôture existe) ; la migration
	// `migrate_hr_leave_period.sql` transforme ces reprises en clôtures en bonne et due forme.

	get cpSummary(): { acquired: number; taken: number; balance: number } | null {
		const baseDate = pickedDateStr(this.cpBaseDateCtrl.value);
		return hrCpSummary(
			{
				base_days: this.cpBaseDaysCtrl.value != null ? +this.cpBaseDaysCtrl.value : (null as any),
				base_date: baseDate ?? '',
				monthly: this.cpMonthlyCtrl.value != null ? +this.cpMonthlyCtrl.value : 0,
			},
			this.allAbsences, HR_ABSENCE_TYPE_CP, HR_ABSENCE_STATE_APPROVED, todayStr(),
		);
	}

	// Décomposition pédagogique de « acquis » : dû à la date de référence + gain mensuel × mois écoulés.
	// Répond à « d'où vient le solde ? » et met en avant le rythme d'acquisition (j/mois).
	get cpDetail(): { baseDays: number; baseDateLabel: string; monthly: number; monthlyLabel: string; months: number; gained: number; acquired: number } | null {
		const baseDate = pickedDateStr(this.cpBaseDateCtrl.value);
		if (this.cpBaseDaysCtrl.value == null || !baseDate) return null;
		const baseDays = +this.cpBaseDaysCtrl.value;
		const monthly = this.cpMonthlyCtrl.value != null ? +this.cpMonthlyCtrl.value : 0;
		const months = hrMonthsElapsed(baseDate, todayStr());
		const [y, m, d] = baseDate.split('-');
		return {
			baseDays, baseDateLabel: `${d}/${m}/${y}`, monthly, months,
			// Le taux s'écrit à la française — « 2,08 », comme partout ailleurs dans le module.
			monthlyLabel: ('' + monthly).replace('.', ','),
			gained: Math.round(monthly * months * 100) / 100,
			acquired: Math.round((baseDays + monthly * months) * 100) / 100,
		};
	}

	// `{ monthly }` seulement quand la personne a un taux À ELLE. Égal à celui de la maison, on ne
	// l'écrit pas : la fiche suivra l'établissement, y compris s'il change d'unité de décompte.
	private cpMonthlyOverride(): { monthly?: number } {
		const v = this.cpMonthlyCtrl.value;
		return v == null || +v === this.leaveRules.days_per_month ? {} : { monthly: +v };
	}

	// Idem pour la règle de report : `{ carryover }` seulement si la case « règle propre » est
	// cochée. Décochée, RIEN n'est écrit — la fiche suit l'établissement, aujourd'hui et après
	// chaque changement de la règle maison.
	private cpCarryoverOverride(): { carryover?: 'carry' | 'lost' } {
		if (!this.cpCarryoverOwnCtrl.value) return {};
		return { carryover: this.cpCarryoverCtrl.value === 'lost' ? 'lost' : 'carry' };
	}

	// Le bloc `data.cp` complet — reprise héritée ET règle de report. La règle de report doit
	// survivre SANS reprise : elle n'a rien à voir avec un solde de départ, et exiger l'un pour
	// enregistrer l'autre perdait le choix au premier enregistrement.
	private cpData(): any {
		const days = this.cpBaseDaysCtrl.value;
		const date = pickedDateStr(this.cpBaseDateCtrl.value);
		const base = (days != null && date)
			? { base_days: +days, base_date: date, ...this.cpMonthlyOverride() }
			: {};
		const cp = { ...base, ...this.cpCarryoverOverride() };
		return Object.keys(cp).length ? cp : null;
	}

	// D'où vient la règle appliquée à ce salarié, en clair — la même phrase que le formulaire de
	// clôture affichera. Sans elle, une case cochée sur une fiche et une autre règle appliquée à
	// la clôture n'auraient aucun moyen de se rejoindre dans la tête de qui les lit.
	get cpCarryoverAside(): string {
		const own = this.cpCarryoverOwnCtrl.value;
		const value = own ? this.cpCarryoverCtrl.value : this.leaveRules.carryover_default;
		const what = value === 'lost' ? 'perdus' : 'reportés sur l\'année suivante';
		return own
			? 'Règle propre à ce salarié : ses jours non pris seront proposés ' + what + '.'
			: 'Règle de l\'établissement : les jours non pris sont proposés ' + what + '.';
	}

	// Le taux de CETTE personne face à celui de l'établissement. Un écart n'est pas une faute — un
	// accord individuel, une reprise d'ancienneté en donnent de bonnes raisons — mais il doit SE
	// VOIR : le taux est enregistré sur la fiche dès qu'on saisit une reprise de congés, et il
	// l'emporte ensuite sur celui de l'entreprise. Changer l'unité de décompte de l'établissement
	// laisse donc les fiches déjà remplies sur leur ancien taux, sans que rien ne le dise.
	get cpMonthlyAside(): string | null {
		const mine = this.cpMonthlyCtrl.value;
		if (mine == null || +mine === this.leaveRules.days_per_month) return null;
		const house = ('' + this.leaveRules.days_per_month).replace('.', ',');
		const unit = this.leaveRules.acquisition_unit === 'ouvres' ? 'ouvrés' : 'ouvrables';
		return 'L\'établissement acquiert ' + house + ' j/mois (jours ' + unit + ') : ce salarié a un taux propre.';
	}

	get isLeft(): boolean {
		const leave = pickedDateStr(this.leaveDateCtrl.value);
		return this.formGroup.get('is_active')?.value === false || (!!leave && leave <= todayStr());
	}

	// ============================================================
	// Absences — historique + saisie inline (reprise de données)
	// ============================================================

	private rebuildAbsences(): void {
		const year = +todayStr().slice(0, 4);		// année MÉTIER, pas celle de l'horloge du poste
		const from = year + '-01-01';
		this.absenceRows = this.allAbsences
			.filter(a => this.showAllYears || a.dateend >= from)
			.map(a => this.toAbsenceRow(a))
			.sort((x, y) => y.absence.datestart.localeCompare(x.absence.datestart));
		// Les compteurs restent ceux de l'année, approuvés seulement.
		this.absenceCounters = this.buildAbsenceCounters(
			this.allAbsences.filter(a => a.state === HR_ABSENCE_STATE_APPROVED && a.dateend >= from), this.absenceTypes);
	}

	private toAbsenceRow(a: HrAbsence): EmployeeAbsenceRow {
		const typ = this.absenceTypes.find(t => t.id === a.typ);
		return {
			absence: a,
			typLabel: typ?.name ?? ('Type #' + a.typ),
			typColor: typ?.data?.color ?? '#9e9e9e',
			period: hrPeriodLabel(a.datestart, a.dateend),
			days: HrAbsence.daysInclusive(a.datestart, a.dateend),
			state: a.state,
			stateLabel: HR_ABSENCE_STATE_LABELS[a.state] ?? ('État ' + a.state),
		};
	}

	private buildAbsenceCounters(absences: HrAbsence[], types: C_value[]): AbsenceCounter[] {
		const byTyp = new Map<number, number>();
		for (const a of absences) {
			const t = a.typ ?? 0;
			byTyp.set(t, (byTyp.get(t) ?? 0) + HrAbsence.daysInclusive(a.datestart, a.dateend));
		}
		return Array.from(byTyp.entries())
			.map(([typ, days]) => ({ typ, days, label: types.find(t => t.id === typ)?.name ?? ('Type #' + typ) }))
			.sort((a, b) => b.days - a.days);
	}

	onToggleAllYears(): void {
		this.showAllYears = !this.showAllYears;
		this.rebuildAbsences();
	}

	// Jours couverts par la ligne en cours de saisie (retour immédiat pendant la frappe).
	get newAbsenceDays(): number {
		return HrAbsence.daysInclusive(pickedDateStr(this.newAbsStart.value), pickedDateStr(this.newAbsEnd.value ?? this.newAbsStart.value));
	}

	// Le motif de refus qu'oppose le plafond du type à la ligne en cours de saisie, ou null.
	// Le compte se lit sur TOUTES les absences déjà connues du salarié (la fiche les charge sur
	// cinq ans), à la date de début de la ligne : c'est la période de CETTE absence qui décide.
	private absenceQuotaIssueOf(typ: number, datestart: string, dateend: string): string | null {
		const type = this.absenceTypes.find(t => t.id === typ);
		const quota = absenceQuotaOf(type);
		if (!quota) return null;

		const year = +datestart.slice(0, 4);
		const holidays = new Map<string, string>();
		for (let y = year - 1; y <= year + 1; y++) {
			for (const [k, label] of frenchHolidaysOf(y)) holidays.set(k, label);
		}
		const account = absenceQuotaAccountOf(
			quota, this.allAbsences.filter(a => a.typ === typ), datestart,
			this.current?.hire_date ?? null, this.leaveRules, holidays,
		);
		// La ligne de saisie n'a pas de repos hebdomadaires : son décompte est calendaire, comme
		// ce qui sera enregistré (`rest_days` nul). Les fériés, eux, ne se décomptent jamais.
		const cost = hrLeaveBreakdown(datestart, dateend, [], holidays).leave_days;
		return absenceQuotaIssue(cost, account, type?.name);
	}

	// Ajoute une absence à la fiche SANS quitter la page : la ligne se vide et reste prête pour
	// la suivante (6 arrêts à saisir d'affilée). Admin ⇒ approuvée dans la foulée (fait constaté).
	async onAddAbsence(): Promise<void> {
		const employeeId = +(this.formGroup.get('id')?.value ?? 0);
		if (!employeeId) { this.alertService.error('Enregistrez d\'abord la fiche : une absence se rattache à un employé existant.'); return; }
		const typ = this.newAbsTyp.value;
		const datestart = pickedDateStr(this.newAbsStart.value);
		const dateend = pickedDateStr(this.newAbsEnd.value) ?? datestart;		// un seul jour : fin = début
		if (!typ) { this.alertService.error('Choisissez le type d\'absence.'); return; }
		if (!datestart || !dateend) { this.alertService.error('Choisissez les dates.'); return; }
		if (dateend < datestart) { this.alertService.error('La fin est avant le début.'); return; }

		// Congé PLAFONNÉ (le jour d'enfant malade de la convention…) : ici, contrairement à l'écran
		// de demande, on AVERTIT sans bloquer. Cette ligne sert aussi à la reprise de données — un
		// droit d'hier ne se refuse pas au motif qu'il n'existe plus aujourd'hui — et le responsable
		// RH est justement celui qui a le dernier mot. Mais il ne le dépassera plus sans le savoir.
		const issue = this.absenceQuotaIssueOf(typ, datestart, dateend);
		if (issue) {
			const r = await Swal.fire({
				title: 'Plafond dépassé',
				text: issue + '\n\nEnregistrer quand même ?',
				icon: 'warning', showCancelButton: true,
				confirmButtonText: 'Enregistrer quand même', cancelButtonText: 'Corriger',
			});
			if (!r.isConfirmed) return;
		}

		// UN SEUL EVENT quand c'est le responsable RH qui saisit : la ligne naît approuvée. Saisir
		// une absence sur la fiche d'un salarié n'est pas lui demander quelque chose — c'est acter
		// un arrêt, une reprise de données, un congé accordé. L'aller-retour demande → décision ne
		// notait qu'un seul et même geste en deux temps, et alertait les responsables d'une demande
		// que l'un d'eux venait d'écrire.
		const payload = this.hrService.buildAbsencePayload({
			id: 0, id_employee: employeeId, typ, datestart, dateend,
			comment: this.newAbsComment.value,
			state: this.isHrManager ? HR_ABSENCE_STATE_APPROVED : HR_ABSENCE_STATE_REQUESTED,
		});
		this.addingAbsence = true;
		this.hrService.postHrEvent(EVENTTYP_INSERT, OBJECTTYP_HR_ABSENCE, payload).subscribe({
			next: () => {
				this.addingAbsence = false;
				// On garde le TYPE (souvent le même arrêt prolongé) et on vide le reste.
				this.newAbsStart.setValue(null);
				this.newAbsEnd.setValue(null);
				this.newAbsComment.setValue('');
				this.alertService.success('Absence ajoutée');
				this.reloadAbsences();
			},
			error: (e) => { this.addingAbsence = false; this.alertService.error(hrErrorMessage(e)); },
		});
	}

	onDeleteAbsence(row: EmployeeAbsenceRow): void {
		Swal.fire({
			title: 'Supprimer cette absence ?',
			text: row.typLabel + ' — ' + row.period + ' (' + row.days + ' j)',
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler',
		}).then(r => {
			if (!r.isConfirmed) return;
			this.hrService.postHrEvent(EVENTTYP_DELETE, OBJECTTYP_HR_ABSENCE, { id: row.absence.id }).subscribe({
				next: () => { this.alertService.success('Absence supprimée'); this.reloadAbsences(); },
				error: (e) => this.alertService.error(hrErrorMessage(e)),
			});
		});
	}

	// Recharge la seule liste des absences (la fiche en saisie n'est pas touchée).
	private reloadAbsences(): void {
		const id = +(this.formGroup.get('id')?.value ?? 0);
		if (!id) return;
		const year = +todayStr().slice(0, 4);		// année MÉTIER, pas celle de l'horloge du poste
		this.hrService.getAbsences$((year - 5) + '-01-01', (year + 1) + '-12-31', undefined, id).subscribe(absences => {
			this.allAbsences = absences;
			this.rebuildAbsences();
		});
	}

	// ============================================================
	// Libellés
	// ============================================================

	// Nom de l'emploi du temps avec son service (le select est groupé par établissement).
	scheduleLabel(s: HrSchedule): string {
		const activity = this.activities.find(a => a.id === hrScheduleActivityId(s))?.name ?? '';
		return s.name + (activity ? ' (' + activity + ')' : '');
	}

	userLabel(u: User): string {
		return ((u.firstname ?? '') + ' ' + (u.lastname ?? '')).trim() || ('User #' + u.id);
	}

	// Titre de la page : le NOM de la personne dès qu'elle est connue.
	get pageTitle(): string {
		if (!this.formGroup.get('id')?.value) return 'Nouvel employé';
		const u = this.users.find(x => x.id === this.formGroup.get('id_user')?.value);
		return u ? this.userLabel(u) : 'Fiche employé';
	}

	// Crée le c_user minimal (sans login) puis le lie à la fiche en cours de saisie.
	onCreateUser(): void {
		const lastname = (this.newUserLast.value ?? '').trim();
		if (!lastname) { this.alertService.error('Le nom est requis.'); return; }
		const firstname = (this.newUserFirst.value ?? '').trim() || null;
		const store = this.formGroup.get('id_store')?.value ?? this.stores[0]?.id ?? 1;
		this.creatingUser = true;
		this.hrService.postHrEvent(EVENTTYP_INSERT, ObjectType.USER, { store: +store, firstname, lastname }).subscribe({
			next: (res) => {
				this.creatingUser = false;
				const id = res?.data?.id;
				if (!id) { this.alertService.error('Création utilisateur : réponse inattendue du serveur.'); return; }
				const u: User = { id: +id, firstname: firstname ?? undefined, lastname, store: +store, roles: [] } as any;
				this.users = [...this.users, u].sort((a, b) => this.userLabel(a).localeCompare(this.userLabel(b)));
				this.formGroup.get('id_user')?.setValue(+id);
				this.personMode = 'existing';		// la personne créée apparaît sélectionnée
				this.newUserFirst.setValue(null);
				this.newUserLast.setValue(null);
				this.alertService.success('Personne créée et liée — cochez ses rôles puis enregistrez la fiche.');
			},
			error: (e) => { this.creatingUser = false; this.alertService.error(hrErrorMessage(e)); },
		});
	}

	// Ce que le salarié saisit lui-même : ses coordonnées, et rien d'autre.
	// Adresse : null tant qu'aucun champ n'est rempli (pas d'objet vide en base).
	private contactData(): { phone: string; email: string; address: { street: string; zip: string; city: string } | null } {
		const street = (this.streetCtrl.value ?? '').trim();
		const zip = (this.zipCtrl.value ?? '').trim();
		const city = (this.cityCtrl.value ?? '').trim();
		return {
			phone: (this.phoneCtrl.value ?? '').trim(),
			email: (this.emailCtrl.value ?? '').trim(),
			address: (street || zip || city) ? { street, zip, city } : null,
		};
	}

	onSubmit(): void {
		if (this.saving) return;
		// Le salarié sur sa propre fiche n'envoie QUE ses coordonnées : ni contrat, ni salaire, ni
		// affectation ne sont montés à l'écran, il n'y a donc rien d'autre à transmettre. Le
		// serveur applique la même règle de son côté (hr/model.rs :: merge_self_editable) —
		// l'écran n'est pas la barrière, il est d'accord avec elle.
		if (!this.isHrManager) { this.saveOwnContact(); return; }

		const payload = this.hrService.buildEmployeePayload(this.formGroup.getRawValue());
		if (!payload.id_user) { this.alertService.error('Choisissez la personne liée à cette fiche.'); return; }

		// Contact facultatif (simplification dev) : la saisie n'est plus bloquante.
		const { phone, email, address } = this.contactData();

		// Naissance, sexe et sortie : COLONNES depuis le 2026-07-27 — premier niveau du payload,
		// plus des clés de data. (L'identité de naissance reste exigée par l'émetteur de
		// titres-restaurant : elle part désormais typée et contrainte.)
		payload.gender = (this.genderCtrl.value === 'M' || this.genderCtrl.value === 'F') ? this.genderCtrl.value : null;
		payload.birth_date = pickedDateStr(this.birthDateCtrl.value);
		payload.leave_date = pickedDateStr(this.leaveDateCtrl.value);
		payload.data = {
			...(this.current?.data ?? {}),
			phone,
			email,
			address,
			// Le taux mensuel n'est enregistré QUE s'il s'écarte de celui de l'établissement : sans
			// cela, chaque fiche figeait une copie du taux du jour, et changer l'unité de décompte de
			// l'entreprise laissait tout le monde sur l'ancien chiffre. Sans valeur propre, la fiche
			// suit l'établissement — c'est le cas normal, et il n'a plus besoin d'être entretenu.
			cp: this.cpData(),
			// Couleur d'identité : c'est elle qui distingue les personnes en vue Postes.
			// null = repli sur la palette stable (hrEmployeeColor).
			color: this.colorCtrl.value || null,
			// L1.2 — données attendues par le rapport de paie (data.pay).
			pay: {
				...(this.current?.data?.pay ?? {}),
				contract_type: (this.contractTypeCtrl.value ?? '').trim() || null,
				gross_monthly: this.grossMonthlyCtrl.value != null && this.grossMonthlyCtrl.value !== ('' as any)
					? +this.grossMonthlyCtrl.value : null,
				qualification: (this.qualificationCtrl.value ?? '').trim() || null,
				trial_end: pickedDateStr(this.trialEndCtrl.value),
				meal_vouchers: this.mealVouchersCtrl.value !== false,
				meal_physical_card: this.mealPhysicalCardCtrl.value === true,
				payroll_code: (this.payrollCodeCtrl.value ?? '').toString().trim() || null,
				hourly_rate: this.hourlyRateCtrl.value != null && this.hourlyRateCtrl.value !== ('' as any)
					? +this.hourlyRateCtrl.value : null,
				level: (this.levelCtrl.value ?? '').toString().trim() || null,
				coefficient: (this.coefficientCtrl.value ?? '').toString().trim() || null,
				position: (this.positionCtrl.value ?? '').toString().trim() || null,
			},
			// N2 — temps de travail propre à ce contrat. `null` partout = la convention s'applique.
			work: {
				...(this.current?.data?.work ?? {}),
				// Statut et décompte. `getRawValue()` pour le décompte : verrouillé sur un cadre
				// dirigeant ou un TNS, un contrôle désactivé rend `undefined` avec `.value`, ce qui
				// effacerait le « hors décompte » à chaque enregistrement.
				status: this.statusCtrl.value ?? 'non_cadre',
				time_scheme: this.timeSchemeCtrl.getRawValue() ?? 'hours',
				forfait_days: this.timeSchemeCtrl.getRawValue() === 'forfait_days'
					? (this.forfaitDaysCtrl.value ?? HR_FORFAIT_DAYS_DEFAULT) : null,
				contract_kind: this.contractKindCtrl.value,
				complementary_limit_ratio: this.complementaryLimitCtrl.value,
				week_working_days: this.weekWorkingDaysCtrl.value,
				day_base_min: this.dayBaseHoursCtrl.value != null && this.dayBaseHoursCtrl.value !== ('' as any)
					? Math.round(+this.dayBaseHoursCtrl.value * 60) : null,
				rcr_default: this.rcrDefaultCtrl.value,
				overtime_leave_basis: this.leaveBasisCtrl.value,
			},
		};
		// Une fiche d'avant la promotion peut encore porter ces clés dans son data : le spread
		// ci-dessus les rallumerait — la colonne fait foi, le vestige s'efface.
		delete payload.data.gender;
		delete payload.data.birth_date;
		delete payload.data.leave_date;
		// Cohérence : le magasin par défaut fait toujours partie de la mobilité.
		if (payload.id_store && !payload.mobility.includes(payload.id_store)) payload.mobility.push(payload.id_store);
		this.saving = true;
		this.hrService.postHrEvent(payload.id === 0 ? EVENTTYP_INSERT : EVENTTYP_UPDATE, OBJECTTYP_HR_EMPLOYEE, payload)
			.subscribe({
				next: (res) => {
					this.saving = false;
					this.alertService.success('Fiche enregistrée');
					if (payload.id === 0 && res?.data?.id) this.router.navigate(['/hr/employee/' + res.data.id]);
				},
				error: (e) => {
					this.saving = false;
					this.alertService.error(hrErrorMessage(e));
				},
			});
	}

	// Enregistrement par le salarié lui-même : un payload volontairement PAUVRE. Il ne porte que
	// l'id de la fiche et les quatre clés que le serveur accepte de sa part — pas de contrat, pas
	// de mobilité, pas de `is_active` à recopier de travers depuis un formulaire qu'il n'a pas vu.
	private saveOwnContact(): void {
		const id = +(this.formGroup.get('id')?.value ?? 0);
		if (!id) return;
		const { phone, email, address } = this.contactData();
		this.saving = true;
		this.hrService.postHrEvent(EVENTTYP_UPDATE, OBJECTTYP_HR_EMPLOYEE, {
			id,
			data: { phone, email, address, color: this.colorCtrl.value || null },
		}).subscribe({
			next: () => { this.saving = false; this.alertService.success('Coordonnées enregistrées'); },
			error: (e) => { this.saving = false; this.alertService.error(hrErrorMessage(e)); },
		});
	}

	// ============================================================
	// DONNÉES SENSIBLES (salaire) — responsable RH + mot de passe
	// ============================================================
	//
	// La carte « Données salariales » n'est montée que pour le RESPONSABLE RH (isHrManager) ET
	// reste masquée tant que le mot de passe n'a pas été retapé. La barrière RÉELLE est serveur :
	// GET /hr/employees ne renvoie `data.pay` à personne d'autre — pas même à l'intéressé, qui lit
	// son salaire sur son bulletin de paie, pas dans un écran de planning.
	public async revealSalary(): Promise<void> {
		const { value: password } = await Swal.fire({
			icon: 'warning',
			title: 'Données salariales',
			text: 'Retapez votre mot de passe pour afficher ces informations sensibles.',
			input: 'password',
			inputPlaceholder: 'Votre mot de passe',
			inputAttributes: { autocomplete: 'current-password' },
			showCancelButton: true,
			confirmButtonText: 'Afficher',
			cancelButtonText: 'Annuler',
		});
		if (!password) return;
		this.sensitive.unlock$(password).subscribe(ok => {
			if (!ok) Swal.fire({ icon: 'error', title: 'Mot de passe incorrect', timer: 2000, showConfirmButton: false });
		});
	}

	onBack(): void {
		this.router.navigate(['/hr/employees']);
	}

	// ============================================================
	// COMPTEUR D'HEURES (L2)
	// ============================================================
	//
	// Le solde n'est jamais lu en base : il se RECALCULE depuis les plannings, les pointages et
	// les absences. Seuls les ajustements manuels sont stockés (hr_counter).

	private rebuildCounter(): void {
		const emp = this.current;
		if (!emp) { this.counterCells = []; return; }

		const year = +todayStr().slice(0, 4);		// année MÉTIER, pas celle de l'horloge du poste
		const from = year + '-01-01';
		const to = year + '-12-31';

		// L'emploi du temps par défaut donne l'ancre et la longueur du cycle : c'est lui qui
		// détermine quelle semaine de modulation s'applique.
		const sch = this.schedules.find(s => s.id === emp.id_schedule) ?? null;

		const slots = effectiveSlotsForRange(from, to, this.schedules, this.overrides, this.allAbsences);
		const { periods } = pairClocks(this.clocks, todayStr());
		// Le forfait jours a son propre compteur, en JOURNÉES — il se recalcule sur les mêmes
		// créneaux que le compteur d'heures, qui lui rendra des cellules vides (cf. buildCounter).
		this.rebuildForfait(slots, from, to);

		this.counterCells = buildCounter({
			employee: emp,
			slots, periods,
			absences: this.allAbsences,
			absenceTypes: this.absenceTypes,
			adjustments: this.counters.map(c => ({ dat: c.dat, minutes: c.minutes })),
			anchorMonday: sch?.anchor_monday ?? null,
			cycleWeeks: sch?.cycle_weeks ?? 1,
			from, to,
			granularity: this.counterGranularity,
			source: this.timeSource,
			validations: this.validations,
			// Le compteur porte l'ANNÉE ENTIÈRE : sans ces bornes, les mois à venir seraient
			// comptés comme travaillés, et une semaine en cours soldée avant d'être finie.
			today: todayStr(),
			now_min: wallMinutesOf(new Date()) ?? 24 * 60,
			// Le compteur applique les MÊMES règles que la paie et le planning : seuils, assiette
			// (congés payés compris), et les surcharges propres à ce contrat.
			rules: this.payrollRules,
			profile: employeeWorkProfileOf(emp.data),
			// Les fériés chômés ne se décomptent pas des congés : sans eux, le 14 juillet d'une
			// semaine de vacances serait valorisé comme un jour de congé. Le calendrier de
			// l'établissement (source officielle + décisions d'ouverture) prime sur le calcul local.
			holidayRules: this.payrollRules.holidays.days.length > 0 ? this.payrollRules.holidays : null,
			holidays: this.payrollRules.holidays.days.length > 0
				? undefined
				: new Map([...frenchHolidaysOf(year), ...frenchHolidaysOf(year + 1)]),
			// Repos compensateurs PRIS : les absences d'un type marqué « récupération »
			// (c_values 23, data.rcr = true) débitent le solde acquis.
			rcrTaken: this.rcrTakenOf(emp.id, slots),
			// Les heures que le responsable RH a explicitement mises en récupération dans le
			// rapport de paie : elles priment sur la politique de l'établissement.
			restDecided: this.restDecided,
		});

		this.rebuildAvg12(emp, slots, periods, from, to);
	}

	// ============================================================
	// Moyenne sur 12 semaines glissantes (L3121-22) — le contrôle qu'aucune semaine ne voit
	// ============================================================
	//
	// 44 h de moyenne sur 12 semaines consécutives : un plafond qu'on ne franchit JAMAIS d'un coup.
	// Douze semaines à 45 h sont douze semaines parfaitement légales prises une à une — c'est leur
	// suite qui ne l'est pas. Aucun écran hebdomadaire ne peut donc le voir, et c'est la raison
	// d'être de ce contrôle : il ne vit qu'ici, sur la fiche, parce que la fiche est le seul écran
	// qui charge l'année entière d'une personne (absences, exceptions et pointages compris).
	//
	// La granularité SEMAINE est recalculée exprès : l'affichage du compteur peut être au mois ou
	// à l'année selon ce que le gestionnaire a choisi, et une moyenne de 12 semaines ne se déduit
	// pas de douzièmes de mois.
	private rebuildAvg12(emp: HrEmployee, slots: EffectiveSlot[], periods: WorkPeriod[], from: string, to: string): void {
		const profile = employeeWorkProfileOf(emp.data);
		// Forfait JOURS, cadre dirigeant, TNS : hors décompte horaire par la loi — un plafond
		// hebdomadaire moyen n'a rien à leur dire.
		if (!countsHours(profile.time_scheme)) { this.avg12Issues = []; return; }

		const sch = this.schedules.find(s => s.id === emp.id_schedule) ?? null;
		const weekly = buildCounter({
			employee: emp,
			slots, periods,
			absences: this.allAbsences,
			absenceTypes: this.absenceTypes,
			adjustments: [],			// une correction manuelle de compteur ne change pas le temps TRAVAILLÉ
			anchorMonday: sch?.anchor_monday ?? null,
			cycleWeeks: sch?.cycle_weeks ?? 1,
			from, to,
			granularity: 'week',
			source: this.timeSource,
			validations: this.validations,
			today: todayStr(),
			now_min: wallMinutesOf(new Date()) ?? 24 * 60,
			rules: this.payrollRules,
			profile,
			holidayRules: this.payrollRules.holidays.days.length > 0 ? this.payrollRules.holidays : null,
			holidays: this.payrollRules.holidays.days.length > 0
				? undefined
				: new Map([...frenchHolidaysOf(+from.slice(0, 4)), ...frenchHolidaysOf(+from.slice(0, 4) + 1)]),
		});

		// Les semaines À VENIR ne comptent pas : une moyenne ne se calcule pas sur du prévisionnel.
		// `key` est le lundi de la semaine (cf. bucketsOf de hr-counter.ts).
		const today = todayStr();
		const weeks = weekly
			.filter(c => c.to <= today)
			.map(c => ({ monday: c.key, basis_min: c.basis_min }));

		this.avg12Issues = avg12Issues(weeks, effectiveWorkRules(this.payrollRules.week_legal_min, this.payrollRules, profile));
	}

	// Les heures de repos compensateur effectivement prises, lues sur le planning effacé par une
	// absence de type « récupération ».
	private rcrTakenOf(empId: number, slots: EffectiveSlot[]): { dat: string; minutes: number }[] {
		const rcrTypes = new Set(this.absenceTypes.filter(t => t.data?.rcr === true).map(t => t.id));
		if (rcrTypes.size === 0) return [];
		const approved = this.allAbsences.filter(a => a.id_employee === empId && a.state === HR_ABSENCE_STATE_APPROVED);
		return slots
			.filter(s => s.id_employee === empId && s.absent)
			.map(s => {
				const a = approved.find(x => HrAbsence.covers(x, s.dat));
				return (a && rcrTypes.has(a.typ ?? -1))
					? { dat: s.dat, minutes: Math.max(0, (s.end_min - s.start_min) - (s.break_min ?? 0)) }
					: null;
			})
			.filter((x): x is { dat: string; minutes: number } => x !== null);
	}

	// ============================================================
	// Statut × décompte du temps
	// ============================================================

	// Le statut a changé : cadre dirigeant et TNS sont HORS DÉCOMPTE par la loi (L3111-2 pour le
	// premier, absence de contrat de travail pour le second) — le champ se verrouille sur 'none'.
	// Pour tous les autres, le décompte redevient un choix contractuel et on repart des heures.
	onStatusChange(): void {
		const forced = timeSchemeForcedBy(this.statusCtrl.value ?? 'non_cadre');
		if (forced) this.timeSchemeCtrl.setValue(forced, { emitEvent: false });
		else if (this.timeSchemeCtrl.value === 'none') this.timeSchemeCtrl.setValue('hours', { emitEvent: false });
		this.syncSchemeLock();
	}

	// Un forfait jours sans plafond n'existe pas : 218 jours est le supplétif (L3121-64).
	onSchemeChange(): void {
		if (this.timeSchemeCtrl.value === 'forfait_days' && !this.forfaitDaysCtrl.value) {
			this.forfaitDaysCtrl.setValue(HR_FORFAIT_DAYS_DEFAULT);
		}
	}

	private syncSchemeLock(): void {
		if (timeSchemeForcedBy(this.statusCtrl.value ?? 'non_cadre')) this.timeSchemeCtrl.disable({ emitEvent: false });
		else this.timeSchemeCtrl.enable({ emitEvent: false });
	}

	get schemeLocked(): boolean {
		return timeSchemeForcedBy(this.statusCtrl.value ?? 'non_cadre') !== null;
	}

	get countsHours(): boolean {
		return countsHours(this.timeSchemeCtrl.value ?? 'hours');
	}

	get isForfaitDays(): boolean {
		return this.timeSchemeCtrl.value === 'forfait_days';
	}

	// Le compteur du forfait jours — l'équivalent des JRTT pour qui décompte des journées.
	// Recalculé avec le compteur (rebuildCounter), pas dans un getter : il balaie une année de
	// créneaux, ce n'est pas un travail à refaire à chaque cycle de détection de changement.
	public forfait: ForfaitReport | null = null;

	// Année civile : période de référence usuelle d'un forfait, et celle des congés payés qui
	// entrent dans le calcul des jours de repos.
	private rebuildForfait(slots: EffectiveSlot[], from: string, to: string): void {
		const emp = this.current;
		if (!emp || !this.isForfaitDays) { this.forfait = null; return; }
		const mine = slots.filter(s => s.id_employee === emp.id);

		// Jours OUVRÉS réellement travaillés — le planning, absences déduites.
		const worked = Array.from(new Set(mine.filter(s => !s.absent).map(s => s.dat)));

		// Jours de repos « forfait » déjà posés : les absences de type RTT approuvées. Le
		// référentiel d'absences les porte sous le code 'rtt' (hr-absence-catalog.ts).
		const rttTypes = new Set(this.absenceTypes
			.filter(t => t.data?.code === 'rtt' || /rtt/i.test(t.name ?? ''))
			.map(t => t.id));
		const taken = this.allAbsences
			.filter(a => a.id_employee === emp.id && a.state === HR_ABSENCE_STATE_APPROVED
				&& rttTypes.has(a.typ ?? -1) && a.datestart <= to && a.dateend >= from)
			.reduce((sum, a) => sum + hrLeaveBreakdown(
				a.datestart < from ? from : a.datestart, a.dateend > to ? to : a.dateend,
				a.rest_days ?? [], frenchHolidaysOf(+from.slice(0, 4)), a.duration_kind, a.hours).leave_days, 0);

		this.forfait = buildForfaitReport({
			forfait_days: this.forfaitDaysCtrl.value ?? HR_FORFAIT_DAYS_DEFAULT,
			from, to,
			worked_dats: worked,
			rest_weekdays: hrGuessRestWeekdays(mine.map(s => ({ weekday: isoWeekdayOf(s.dat) }))),
			closed_holiday_dats: closedHolidaysInRange(this.payrollRules.holidays, from, to).map(d => d.dat),
			// Cinq semaines de congés payés, dans l'unité de décompte de l'établissement.
			leave_days_due: this.payrollRules.week_working_days * 5,
			rest_days_taken: taken,
		});
	}

	// Ce que ce réglage CHANGE concrètement, dit en une phrase sous le champ. C'est la seule façon
	// qu'un gérant ait de vérifier qu'il n'a pas coché « hors décompte » par erreur.
	get schemeHint(): string {
		switch (this.timeSchemeCtrl.value) {
			case 'forfait_hours':
				return 'Les heures sont comptées : le forfait fixe le volume payé d\'avance, ce qui dépasse reste dû (L3121-56).';
			case 'forfait_days':
				return 'On décompte des JOURNÉES, jamais des heures : aucune heure supplémentaire ne peut naître. '
					+ 'Le dépassement du forfait se solde en jours de repos. Planning et congés fonctionnent normalement.';
			case 'none':
				return this.statusCtrl.value === 'tns'
					? 'Gérant non salarié : pas de contrat de travail, donc ni compteur d\'heures, ni heures supplémentaires, '
						+ 'ni congés payés, ni ligne dans le rapport de paie. Reste planifiable pour savoir qui tient la boutique.'
					: 'Cadre dirigeant (L3111-2) : hors des titres II et III du code du travail — ni durée maximale, '
						+ 'ni heures supplémentaires, ni compteur. Reste planifiable, sans aucun total.';
			default:
				return 'Le cas général : horaire collectif, heures comptées, majorations dues. '
					+ 'C\'est aussi le régime du CADRE INTÉGRÉ — le statut cadre ne dispense jamais de compter.';
		}
	}

	// « Un jour de congé vaut 7 h » — le repère, calculé sur SON contrat et SES jours travaillés.
	// « Une semaine = 5 jours de 7 h » — la phrase que le gérant doit lire sur la fiche, et qui
	// sert à valoriser les semaines partielles (jours de congés + jours travaillés).
	get dayBaseHint(): string {
		const hours = +(this.formGroup.get('contract_hours_week')?.value ?? 0);
		if (!hours) return 'Renseignez d\'abord les heures hebdomadaires';
		const days = Math.min(7, Math.max(1, this.weekWorkingDaysCtrl.value ?? this.payrollRules.week_working_days));
		const perDay = this.dayBaseHoursCtrl.value != null && +this.dayBaseHoursCtrl.value > 0
			? +this.dayBaseHoursCtrl.value : hours / days;
		return 'Une semaine = ' + days + ' jours de ' + this.hoursText(perDay)
			+ ' en moyenne. Un jour de congé vaut ' + this.hoursText(perDay)
			+ '. Un jour férié chômé vaut les heures que le planning prévoyait ce jour-là — '
			+ this.hoursText(perDay) + ' seulement à défaut de planning.';
	}

	private hoursText(hours: number): string {
		const h = Math.floor(hours);
		const m = Math.round((hours - h) * 60);
		return h + ' h' + (m ? ' ' + ('' + m).padStart(2, '0') : '');
	}

	// Le volet récupération ne s'affiche que s'il a de la matière : sans repos compensateur en
	// place, trois lignes vides ne diraient rien à personne.
	// Le volet récupération s'affiche dès qu'UNE de ses trois origines a quelque chose à dire —
	// heures supplémentaires récupérées, repos d'une majoration, contrepartie obligatoire.
	get hasRcr(): boolean {
		return this.counterCells.some(c => c.rcr_credited_min > 0 || c.rcr_taken_min > 0
			|| c.premium_rest_min > 0 || c.cor_min > 0);
	}

	get hasPremiumRest(): boolean {
		return this.counterCells.some(c => c.premium_rest_min > 0);
	}

	get hasCor(): boolean {
		return this.counterCells.some(c => c.cor_min > 0);
	}

	onCounterGranularity(g: CounterGranularity): void {
		this.counterGranularity = g;
		this.rebuildCounter();
	}

	counterLabel(minutes: number): string {
		return counterLabel(minutes);
	}

	// Contraste du texte sur la couleur choisie : un jaune vif et un bleu marine ne portent
	// pas la même couleur de texte.

	// Solde à la fin de la dernière période affichée — le chiffre que le gérant retient.
	get counterBalance(): number {
		return this.counterCells.length ? this.counterCells[this.counterCells.length - 1].cumulative : 0;
	}

	// Saisie d'une correction : heures SIGNÉES (négatif = heures payées, retirées du stock).
	onAddCounterAdjustment(): void {
		const emp = this.current;
		const hours = this.adjustHoursCtrl.value;
		if (!emp || hours == null || +hours === 0) {
			this.alertService.error('Indiquez un nombre d\'heures non nul (négatif pour retirer du stock)');
			return;
		}
		const payload = this.hrService.buildCounterPayload({
			id: 0, id_employee: emp.id, dat: todayStr(),
			minutes: Math.round(+hours * 60),
			kind: HR_COUNTER_KIND_MANUAL,
			comment: this.adjustCommentCtrl.value,
		});
		this.hrService.postHrEvent(EVENTTYP_INSERT, OBJECTTYP_HR_COUNTER, payload).subscribe({
			// Pas de garde success ici : postHrEvent convertit déjà tout refus métier en `error`.
			next: () => {
				this.adjustHoursCtrl.setValue(null);
				this.adjustCommentCtrl.setValue(null);
				this.alertService.success('Compteur ajusté');
				this.load(this.current!.id);
			},
			error: (e) => this.alertService.error(hrErrorMessage(e)),
		});
	}

	onRemoveCounterAdjustment(c: HrCounter): void {
		Swal.fire({
			title: 'Retirer cet ajustement ?',
			text: counterLabel(c.minutes) + (c.comment ? ' — ' + c.comment : ''),
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Retirer', cancelButtonText: 'Annuler',
		}).then(r => {
			if (!r.isConfirmed) return;
			this.hrService.postHrEvent(EVENTTYP_DELETE, OBJECTTYP_HR_COUNTER, { id: c.id }).subscribe({
				next: () => { this.alertService.success('Ajustement retiré'); this.load(this.current!.id); },
				error: (e) => this.alertService.error(hrErrorMessage(e)),
			});
		});
	}

	// ============================================================
	// DOCUMENTS RH (L5)
	// ============================================================
	//
	// Stockés dans `c_attach` (objecttyp HR_EMPLOYEE) : le stockage de fichiers, les miniatures
	// et le service de contenu existaient déjà. Le classement RH vit dans `data.hr`.

	get documentFolders(): { folder: { id: number; name: string; icon: string }; docs: HrDocument[] }[] {
		return HR_DOC_FOLDERS
			.map(folder => ({ folder, docs: this.documents.filter(d => HrDocument.folderOf(d) === folder.id) }))
			.filter(g => g.docs.length > 0 || g.folder.id === this.docFolderCtrl.value);
	}

	docExpiryState(d: HrDocument): string | null {
		return HrDocument.expiryState(d, todayStr());
	}

	docExpiryLabel(d: HrDocument): string {
		const exp = HrDocument.expiryOf(d);
		if (!exp) return '';
		const state = this.docExpiryState(d);
		const dat = exp.slice(8, 10) + '/' + exp.slice(5, 7) + '/' + exp.slice(0, 4);
		return state === 'expired' ? 'Expiré le ' + dat : 'Expire le ' + dat;
	}

	docDate(d: HrDocument): string {
		return d.datcre ? d.datcre.slice(8, 10) + '/' + d.datcre.slice(5, 7) + '/' + d.datcre.slice(0, 4) : '';
	}

	onPickDocument(event: Event): void {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		if (!file || !this.current) return;

		// Le classement et la date d'expiration voyagent dans `data` de la pièce jointe.
		const data = {
			hr: {
				folder: this.docFolderCtrl.value ?? HR_DOC_FOLDER_OTHER,
				datexpire: pickedDateStr(this.docExpiryCtrl.value),
				visible_employee: true,
			},
		};
		this.uploadingDoc = true;
		this.attachService.postAttach$(file, data, ObjectType.HR_EMPLOYEE, this.current.id).subscribe({
			next: (r) => {
				this.uploadingDoc = false;
				// Convention plateforme : un refus métier répond HTTP 200 + success:false — sans cette
				// garde, on annoncerait « Document déposé » sur un dépôt refusé par le serveur.
				if (r?.success === false) { this.alertService.error(r.error_msg ?? r.msg ?? 'Dépôt refusé'); return; }
				this.docExpiryCtrl.setValue(null);
				input.value = '';			// permet de redéposer le même fichier
				this.alertService.success('Document déposé');
				this.load(this.current!.id);
			},
			error: (e) => {
				this.uploadingDoc = false;
				this.alertService.error(hrErrorMessage(e));
			},
		});
	}

	onDownloadDocument(d: HrDocument): void {
		this.attachService.openInNewTab(d.id);
	}

	onDeleteDocument(d: HrDocument): void {
		Swal.fire({
			title: 'Supprimer « ' + (d.name || 'ce document') + ' » ?',
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler',
		}).then(r => {
			if (!r.isConfirmed) return;
			// `suppressImage` est le DELETE générique d'une pièce c_attach (nom hérité des photos).
			// Il ne lit que l'id ; le reste du constructeur d'Attach ne sert qu'à typer l'appel.
			this.attachService.suppressImage(new Attach({
				id: d.id, userid: this.loginService.loggeduser?.id ?? 0,
				objecttyp: ObjectType.HR_EMPLOYEE, objectid: d.id_employee,
			})).subscribe({
				next: (res) => {
					// Même garde que le dépôt : un refus serveur arrive en HTTP 200 + success:false.
					if (res?.success === false) { this.alertService.error(res.error_msg ?? res.msg ?? 'Suppression refusée'); return; }
					this.alertService.success('Document supprimé');
					this.load(this.current!.id);
				},
				error: (e) => this.alertService.error(hrErrorMessage(e)),
			});
		});
	}

	// ============================================================
	// EXPORT « MES DONNÉES » (R9 — art. 15 et 20 RGPD)
	// ============================================================
	//
	// Visible par la personne sur SA fiche uniquement (responsable RH compris, sur la sienne) :
	// le droit d'accès appartient à l'intéressé. Le responsable RH qui répond à la demande d'un
	// tiers passe par les écrans — l'export « à la place de » serait un autre traitement.

	public exportingMyData = false;

	onExportMyData(): void {
		const emp = this.current;
		if (!emp || !this.isSelf || this.exportingMyData) return;
		// Plage LARGE : le droit d'accès porte sur TOUT ce qui est conservé, pas sur l'année
		// affichée. Dates NUES comparées en chaînes (contrat maison, jamais d'arithmétique tz).
		const from = '2000-01-01';
		const to = (+todayStr().slice(0, 4) + 1) + '-12-31';
		this.exportingMyData = true;
		forkJoin({
			absences: this.hrService.getAbsences$(from, to, undefined, emp.id),
			clocks: this.hrService.getClocks$(from, to, emp.id),
			counters: this.hrService.getCounters$(from, to, emp.id),
			meals: this.hrService.getMeals$(from, to, emp.id),
			contracts: this.hrService.getContracts$(emp.id),
			leavePeriods: this.hrService.getLeavePeriods$(emp.id),
		}).subscribe({
			next: (parts) => {
				this.exportingMyData = false;
				// Blob côté client : rien ne transite ni ne se stocke ailleurs que chez la personne.
				const json = JSON.stringify(hrMyDataExport(emp, parts, nowIso()), null, '\t');
				const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
				const a = document.createElement('a');
				a.href = url;
				a.download = 'mes-donnees-rh-' + todayStr() + '.json';
				a.click();
				URL.revokeObjectURL(url);
			},
			error: (e) => { this.exportingMyData = false; this.alertService.error(hrErrorMessage(e)); },
		});
	}

	// ============================================================
	// DISPONIBILITÉS / INDISPONIBILITÉS (L4)
	// ============================================================
	//
	// « Radia n'est jamais là le mercredi après-midi » : on le déclare une fois, le planning
	// l'affiche toutes les semaines. Informatif — on garde le droit de planifier quand même.

	get availabilityRows(): { a: HrAvailability; label: string }[] {
		return this.availabilities
			.filter(a => a.id_employee === this.current?.id)
			.map(a => ({ a, label: availabilityLabel(a, HR_DAY_LABELS) }));
	}

	onAddAvailability(): void {
		const emp = this.current;
		if (!emp) return;
		const weekday = this.availWeekdayCtrl.value;
		if (weekday == null) { this.alertService.error('Choisissez le jour de la semaine'); return; }

		const payload = this.hrService.buildAvailabilityPayload({
			id: 0, id_employee: emp.id,
			kind: this.availKindCtrl.value ?? HR_AVAILABILITY_UNAVAILABLE,
			weekday,
			start_min: minutesOfHhmm(this.availStartCtrl.value ?? ''),
			end_min: minutesOfHhmm(this.availEndCtrl.value ?? ''),
			datend: this.availUntilCtrl.value,
			comment: this.availCommentCtrl.value,
		});
		this.hrService.postHrEvent(EVENTTYP_INSERT, OBJECTTYP_HR_AVAILABILITY, payload).subscribe({
			// Pas de garde success ici : postHrEvent convertit déjà tout refus métier en `error`.
			next: () => {
				// On garde le jour et le type : on enchaîne souvent plusieurs déclarations.
				this.availStartCtrl.setValue(null);
				this.availEndCtrl.setValue(null);
				this.availCommentCtrl.setValue(null);
				this.alertService.success('Disponibilité enregistrée');
				this.load(emp.id);
			},
			error: (e) => this.alertService.error(hrErrorMessage(e)),
		});
	}

	onRemoveAvailability(a: HrAvailability): void {
		Swal.fire({
			title: 'Retirer cette déclaration ?',
			text: availabilityLabel(a, HR_DAY_LABELS),
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Retirer', cancelButtonText: 'Annuler',
		}).then(r => {
			if (!r.isConfirmed) return;
			this.hrService.postHrEvent(EVENTTYP_DELETE, OBJECTTYP_HR_AVAILABILITY, { id: a.id }).subscribe({
				next: () => { this.alertService.success('Déclaration retirée'); this.load(this.current!.id); },
				error: (e) => this.alertService.error(hrErrorMessage(e)),
			});
		});
	}

	// ============================================================
	// MODULATION (L2.3) — volume hebdo par semaine du cycle
	// ============================================================

	// Longueur du cycle de l'emploi du temps par défaut : au-delà de 1, la modulation a du sens.
	get cycleWeeks(): number {
		const sch = this.schedules.find(s => s.id === this.formGroup.get('id_schedule')?.value);
		return sch?.cycle_weeks ?? 1;
	}

	get cycleHours(): number[] {
		const v = this.formGroup.get('cycle_hours')?.value as number[] | null;
		const n = this.cycleWeeks;
		const base = +(this.formGroup.get('contract_hours_week')?.value ?? 0);
		// Un cycle qui s'allonge se complète avec le contrat de base plutôt que des zéros.
		return Array.from({ length: n }, (_, i) => v?.[i] ?? base);
	}

	onCycleHourChange(i: number, value: any): void {
		const next = [...this.cycleHours];
		next[i] = +value;
		this.formGroup.get('cycle_hours')?.setValue(next);
		this.formGroup.markAsDirty();
	}

	onClearModulation(): void {
		this.formGroup.get('cycle_hours')?.setValue(null);
		this.formGroup.markAsDirty();
	}

	get modulationActive(): boolean {
		const v = this.formGroup.get('cycle_hours')?.value as number[] | null;
		return Array.isArray(v) && v.length > 0;
	}
}
